import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, X, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";

const AdminPolls = () => {
  const [polls, setPolls] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await adminApi.get("/admin/polls");
      setPolls(data);
    } catch {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);

  const setOpt = (i, v) => setOptions((o) => o.map((x, idx) => (idx === i ? v : x)));
  const addOpt = () => options.length < 6 && setOptions((o) => [...o, ""]);
  const rmOpt = (i) => options.length > 2 && setOptions((o) => o.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    const cleaned = options.map((o) => o.trim()).filter(Boolean);
    if (question.trim().length < 3 || cleaned.length < 2) {
      toast.error("Preencha a pergunta e pelo menos 2 opções.");
      return;
    }
    setBusy(true);
    try {
      await adminApi.post("/admin/polls", { question: question.trim(), options: cleaned });
      toast.success("Enquete criada.");
      setQuestion("");
      setOptions(["", ""]);
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (id) => {
    try {
      await adminApi.post(`/admin/polls/${id}/toggle`);
      load();
    } catch {}
  };

  const remove = async (id) => {
    if (!window.confirm("Remover esta enquete? Os votos também serão apagados.")) return;
    try {
      await adminApi.delete(`/admin/polls/${id}`);
      toast.success("Removida.");
      load();
    } catch {}
  };

  return (
    <div>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-[var(--fi-red)] font-bold mb-1">
            Interação
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-black uppercase text-[var(--fi-blue)] leading-tight">
            Enquetes
          </h1>
          <p className="text-gray-500 mt-1">Crie enquetes em tempo real com os cidadãos.</p>
        </div>
        <button
          data-testid={ADMIN.polls.newBtn}
          onClick={() => setShowForm((s) => !s)}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[var(--fi-red)] hover:bg-[var(--fi-red-hover)] text-white font-bold uppercase tracking-widest text-xs"
        >
          <Plus className="w-4 h-4" /> {showForm ? "Fechar" : "Nova enquete"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="mt-5 rounded-xl bg-white p-5 shadow-[0_8px_30px_rgba(13,27,75,0.06)] space-y-3">
          <div>
            <label className="block text-xs uppercase tracking-widest font-bold text-[var(--fi-blue)] mb-1.5">Pergunta *</label>
            <textarea data-testid={ADMIN.polls.question} value={question} onChange={(e) => setQuestion(e.target.value)} rows={2} className="input" placeholder="Ex: Qual obra deve ser priorizada em 2026?" />
          </div>
          <div className="space-y-2">
            {options.map((o, i) => (
              <div key={i} className="flex gap-2">
                <input data-testid={ADMIN.polls.option(i)} value={o} onChange={(e) => setOpt(i, e.target.value)} className="input flex-1" placeholder={`Opção ${i + 1}`} />
                {options.length > 2 && (
                  <button type="button" onClick={() => rmOpt(i)} className="w-10 rounded-lg text-gray-400 hover:text-[var(--fi-red)] flex items-center justify-center" aria-label="Remover opção">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between items-center pt-1">
            <button type="button" data-testid={ADMIN.polls.addOption} onClick={addOpt} disabled={options.length >= 6} className="text-xs uppercase tracking-widest font-bold text-[var(--fi-blue)] hover:underline disabled:opacity-40">
              + Adicionar opção
            </button>
            <button data-testid={ADMIN.polls.submit} disabled={busy} className="inline-flex items-center gap-2 rounded-xl bg-[var(--fi-blue)] hover:bg-[var(--fi-blue-2)] text-white font-bold uppercase tracking-widest text-xs px-5 py-3 transition-colors disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Publicar enquete"}
            </button>
          </div>
          <style>{`.input{width:100%;padding:10px 12px;border:1px solid #e5e7eb;border-radius:10px;font-size:14px;outline:none;transition:border-color .15s}.input:focus{border-color:#0D1B4B}`}</style>
        </form>
      )}

      <div className="mt-6 space-y-3">
        {polls.length === 0 && <div className="text-center py-16 text-gray-400 font-medium">Nenhuma enquete criada ainda.</div>}
        {polls.map((p) => (
          <div key={p.id} data-testid={ADMIN.polls.row(p.id)} className="rounded-xl bg-white p-4 shadow-[0_6px_20px_rgba(13,27,75,0.05)]">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg font-extrabold text-[var(--fi-blue)] leading-tight">{p.question}</div>
                <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                  <BarChart3 className="w-3 h-3" />
                  {p.total_votes} voto(s)
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid={ADMIN.polls.toggle(p.id)} onClick={() => toggle(p.id)} className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-widest font-bold ${p.is_active ? "bg-emerald-500 text-white hover:bg-emerald-600" : "bg-gray-200 text-gray-600 hover:bg-gray-300"} transition-colors`}>
                  {p.is_active ? "Ativa" : "Encerrada"}
                </button>
                <button data-testid={ADMIN.polls.delete(p.id)} onClick={() => remove(p.id)} className="w-9 h-9 rounded-lg text-gray-400 hover:text-[var(--fi-red)] hover:bg-red-50 flex items-center justify-center" aria-label="Remover">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="mt-3 space-y-1.5">
              {p.options.map((o) => {
                const pct = p.total_votes > 0 ? (o.votes / p.total_votes) * 100 : 0;
                return (
                  <div key={o.id}>
                    <div className="flex items-center justify-between text-xs mb-0.5">
                      <span className="text-[var(--fi-blue)] font-semibold">{o.text}</span>
                      <span className="text-gray-500">{o.votes} · {pct.toFixed(1)}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                      <div className="h-full fi-gradient" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminPolls;
